import React, { useState } from 'react';
import { Modal as AntModal } from 'antd';
import Button from '../Button/Button';

/**
 * @typedef {'sm'|'md'|'lg'|'xl'|'full'} ModalSize
 */

const SIZE_WIDTH = {
  sm:   420,
  md:   560,
  lg:   720,
  xl:   960,
  full: '90vw',
};

/**
 * Shared Modal component — standardizes header, footer, and sizes.
 *
 * Footer behaviour:
 *   - footer === undefined  →  render default Cancel + Confirm buttons
 *                               (or just a Close button if onConfirm is absent)
 *   - footer === null       →  no footer rendered
 *   - footer is ReactNode   →  render that custom footer
 *
 * @param {object}           props
 * @param {boolean}          props.open
 * @param {() => void}       props.onClose
 * @param {React.ReactNode}  [props.title]
 * @param {React.ReactNode}  props.children
 * @param {ModalSize}        [props.size='md']
 * @param {number|string}    [props.width]         - overrides size
 * @param {React.ReactNode|null} [props.footer]
 * @param {string}           [props.confirmText='OK']
 * @param {string}           [props.cancelText='Cancel']
 * @param {() => void|Promise<void>} [props.onConfirm]
 * @param {boolean}          [props.loading]
 * @param {boolean}          [props.closable=true]
 * @param {boolean}          [props.maskClosable=true]
 * @param {boolean}          [props.danger]
 */
const Modal = ({
  open,
  onClose,
  title,
  children,
  size = 'md',
  width: widthProp,
  footer,
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm,
  loading,
  closable = true,
  maskClosable = true,
  danger = false,
}) => {
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleConfirm = async () => {
    if (!onConfirm) return;
    const result = onConfirm();
    if (result && typeof result.then === 'function') {
      setConfirmLoading(true);
      try {
        await result;
      } finally {
        setConfirmLoading(false);
      }
    }
  };

  // Resolve footer content
  let resolvedFooter;
  if (footer === null) {
    resolvedFooter = null;
  } else if (footer !== undefined) {
    // Custom footer node passed in
    resolvedFooter = footer;
  } else if (onConfirm) {
    // Default Cancel + Confirm
    resolvedFooter = (
      <>
        <Button variant="secondary" onClick={onClose}>
          {cancelText}
        </Button>
        <Button
          variant={danger ? 'danger' : 'primary'}
          loading={loading || confirmLoading}
          onClick={handleConfirm}
        >
          {confirmText}
        </Button>
      </>
    );
  } else {
    // No onConfirm — just a Close button
    resolvedFooter = (
      <Button variant="secondary" onClick={onClose}>
        {cancelText}
      </Button>
    );
  }

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      title={title}
      width={widthProp ?? SIZE_WIDTH[size]}
      footer={resolvedFooter}
      destroyOnHidden
      centered
      closable={closable}
      maskClosable={maskClosable}
    >
      {children}
    </AntModal>
  );
};

export default Modal;
